import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { EventDto } from '@odo/shared-ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useEvent } from '../../hooks/useEvents';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { EventForm } from '../../components/events/EventForm';
import { EventSignupsPanel } from '../../components/events/EventSignupsPanel';
import { hasSignups } from '../../components/events/eventKind';
import {
    EventDetailHeader,
    type EventDetailTabId
} from '../../components/events/EventDetailHeader';

const EVENTS_LIST_PATH = '/accounts/events';
const DEFAULT_TAB: EventDetailTabId = 'details';

// Validated the same way as PracticeDetail's readTabFromSearch: an unknown
// value, or `signups` without permission/before the event exists, falls back.
function readTabFromSearch(
    search: string,
    isCreate: boolean,
    canViewSignups: boolean
): EventDetailTabId {
    const raw = new URLSearchParams(search).get('tab');
    return !isCreate && canViewSignups && raw === 'signups' ? 'signups' : DEFAULT_TAB;
}

/** Creates and edits an event on one page: `/accounts/events/new` and
 *  `/accounts/events/:eventId` differ only in whether there's an event to load. */
export function EventDetailPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const isCreate = eventId === undefined;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // Set by EventsList's "Duplicate event" row action - content-only seed for
    // a new event, ignored once an existing `event` loads (edit never duplicates).
    const duplicateFrom = isCreate
        ? ((location.state as { duplicateFrom?: EventDto } | null)?.duplicateFrom ?? null)
        : null;

    const { event, isLoading, isError } = useEvent(eventId);
    // Reading an event is open to every files-viewer user; authoring needs
    // `events.update`. `events.get` is separate - it gates only the PII sign-ups read.
    const { hasPermission } = useMyPermissions();
    const canManage = hasPermission('events.update');
    // An announcement takes no sign-ups, so the tab and its panel do not exist
    // for one - regardless of the permission.
    const canViewSignups = hasPermission('events.get') && event !== null && hasSignups(event);

    const activeTab = readTabFromSearch(searchParams.toString(), isCreate, canViewSignups);

    // Mounted once first visited, then kept alive (hidden, not unmounted) - a
    // fresh mount per visit would silently re-fetch and reset its own page state.
    const [signupsVisited, setSignupsVisited] = useState(activeTab === 'signups');
    useEffect(() => {
        if (activeTab === 'signups') setSignupsVisited(true);
    }, [activeTab]);

    usePageTitle(isCreate ? 'New event' : (event?.title ?? 'Event'));

    const goToList = () => navigate(EVENTS_LIST_PATH);

    const handleSaved = (savedId: string) => {
        // Creating lands on the saved event, not the list - the slug is only
        // known now, and the Sign-ups tab only exists from here.
        navigate(`${EVENTS_LIST_PATH}/${savedId}`, { replace: isCreate });
    };

    const setTab = (tab: EventDetailTabId) => {
        const next = new URLSearchParams(searchParams);
        if (tab === DEFAULT_TAB) {
            next.delete('tab');
        } else {
            next.set('tab', tab);
        }
        // `replace`, so switching tabs does not fill the history with steps the
        // back button has to walk through to leave the page.
        setSearchParams(next, { replace: true });
    };

    // `/new` is meaningless without the write permission: there would be nothing
    // to submit. Redirect rather than render a form whose save button is hidden.
    if (isCreate && !canManage) {
        return <Navigate to={EVENTS_LIST_PATH} replace />;
    }

    if (!isCreate && isLoading) {
        return (
            <div className="flex flex-col h-full overflow-hidden bg-gray-100">
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                </div>
            </div>
        );
    }

    if (!isCreate && (isError || !event)) {
        return (
            <div className="flex flex-col h-full overflow-hidden bg-gray-100">
                <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center">
                    <p className="text-sm text-gray-500">
                        {isError ? "Couldn't load this event." : 'This event no longer exists.'}
                    </p>
                    <button
                        type="button"
                        onClick={goToList}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        Back to events
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <EventDetailHeader
                isCreate={isCreate}
                event={event ?? null}
                activeTab={activeTab}
                onTabChange={setTab}
                canViewSignups={canViewSignups}
            />
            {/* Always mounted - toggling `hidden` (not unmounting) keeps unsaved
                edits alive across a round trip to the Sign-ups tab. */}
            <div
                className={`flex-1 min-h-0 flex flex-col ${activeTab === 'details' ? '' : 'hidden'}`}
            >
                <EventForm
                    key={event?.id ?? 'new'}
                    event={isCreate ? null : (event ?? null)}
                    duplicateFrom={duplicateFrom}
                    canManage={canManage}
                    onSaved={handleSaved}
                    onCancel={goToList}
                />
            </div>
            {!isCreate && event && canViewSignups && signupsVisited && (
                <div
                    className={`flex-1 min-h-0 flex flex-col ${activeTab === 'signups' ? '' : 'hidden'}`}
                >
                    <EventSignupsPanel key={event.id} event={event} />
                </div>
            )}
        </div>
    );
}
